import { FC, useContext, useEffect, useRef } from 'react';

import { getFormField } from 'utils/getFormField';
import {
  useGetPermissionsQuery,
  useLazyGetCcyQuery,
  useLazyGetCountryAQuery,
} from '~/api';
import { TDepositInfo } from '~/api/deposits/deposits.types';
import { EntrySubSection } from '~/components/EntryScreen';
import { ValueRow } from '~/components/ValueRow';
import { autoScrollToSomething } from '~/utils/autoScrollToSomething';
import { isEmptyValues } from '~/utils/isEmptyValues';

import { SwiftField } from '../../../SwiftField';
import { getFields } from '../../DepositForm.helpers';
import { DepositFormContext } from '../../contexts/DepositFormContext';

import {
  StyledDepositFormContainer,
  StyledFormField,
} from './DepositDetails.styled';
import { TDepositDetailsProps } from './DepositDetails.types';

export const DepositDetails: FC<TDepositDetailsProps> = ({
  deposit,
  isEditMode,
}) => {
  const { hiddenFields, setIsFormDisabled } = useContext(DepositFormContext);

  const formRef = useRef<HTMLDivElement>(null);
  const { data: permissions } = useGetPermissionsQuery();

  const canChange = Boolean(permissions?.deposit_info.change_depositinfo);

  const [onCountryAFetch, stateCountryA] = useLazyGetCountryAQuery();
  const [onCcyFetch, stateCcy] = useLazyGetCcyQuery();

  useEffect(() => {
    if (isEditMode) {
      onCountryAFetch();
      onCcyFetch();

      autoScrollToSomething(formRef);
    }
  }, [isEditMode]);

  const countryA = {
    isLoading: stateCountryA?.isLoading,
    options: stateCountryA?.data,
  };

  const ccy = {
    isLoading: stateCcy?.isLoading,
    options: stateCcy?.data,
  };

  const depositDetailsFields = getFields({
    countryA,
    ccy,
    deposit,
    canChange,
    hiddenFields,
  });

  useEffect(() => {
    setIsFormDisabled(!depositDetailsFields.length);
  }, [depositDetailsFields]);

  if (!depositDetailsFields.length) {
    return null;
  }

  return (
    <EntrySubSection title="Deposit Details">
      <StyledDepositFormContainer
        className="DepositFormContainer"
        ref={formRef}
      >
        {depositDetailsFields?.map(
          ({
            name,
            node,
            title,
            type,
            formatter,
            optionsConfig,
            editable,
            withCopyButton,
          }) => {
            const value = deposit[name as keyof TDepositInfo];

            if (isEmptyValues(value, true) && typeof value !== 'boolean') {
              return null;
            }

            if (!isEditMode || editable === false) {
              return (
                <ValueRow
                  key={name}
                  label={title}
                  valueNode={node}
                  withCopyButton={withCopyButton}
                  value={formatter ? formatter(value) : value}
                />
              );
            }

            if (name === 'swift' || name === 'bank_name') {
              return (
                <ValueRow
                  key={name}
                  label={title}
                  valueNode={
                    <SwiftField objectId={deposit.application} name={name} />
                  }
                />
              );
            }

            return (
              <ValueRow
                key={name}
                label={title}
                valueNode={
                  <StyledFormField className="FormField">
                    {getFormField({
                      type,
                      name,
                      isLoading: optionsConfig?.isLoading,
                      options: optionsConfig?.options,
                    })}
                  </StyledFormField>
                }
              />
            );
          },
        )}
      </StyledDepositFormContainer>
    </EntrySubSection>
  );
};
